import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { fmtMoney } from "../lib/format";
import { CaretDown, CaretRight, WarningOctagon } from "@phosphor-icons/react";

/**
 * v0.4.14 — replaces the single wide TemplateSummary with vertical
 * filing-category sections. Each section is self-contained:
 *  - own title + filing destination
 *  - own currency note
 *  - own column shape (no cross-category columns)
 *  - own per-country rows from the same `template_summary` data
 *
 * B2B and B2C are NEVER blended (signed-off invariant).
 * OSS is explicitly labelled "filed via separate OSS return".
 */

const Section = ({ id, titleKey, captionKey, instructionKey, defaultOpen, flagBadge = null, children, hasRows }) => {
  const { t } = useTranslation();
  // v0.4.22 F2 — empty sections collapse by default regardless of the
  // section's own `defaultOpen` preference. Sections with data honour
  // their `defaultOpen` (refunds/exports remain collapsed by default).
  const [open, setOpen] = useState(hasRows && defaultOpen);
  return (
    <section
      data-testid={`filing-section-${id}`}
      className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
    >
      <button
        type="button"
        data-testid={`filing-section-toggle-${id}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white dark:bg-slate-900 text-left"
      >
        <div className="flex items-center gap-2">
          {open ? <CaretDown size={14} className="text-teal-600 dark:text-teal-400" /> : <CaretRight size={14} className="text-slate-500 dark:text-slate-400" />}
          <span className={`tracking-normal font-bold ${hasRows ? "text-base text-teal-700 dark:text-teal-300" : "text-xs text-slate-500 dark:text-slate-400"}`}>{t(titleKey)}</span>
          {!hasRows && (
            <span className="text-[10px] text-slate-600 dark:text-slate-300 ml-2">— {t("ui.filing.no_rows")}</span>
          )}
          {flagBadge}
        </div>
        {hasRows && (
          <span className="text-sm text-slate-600 dark:text-slate-300 truncate ml-4">{t(captionKey)}</span>
        )}
      </button>
      {/* v0.4.22 F2 — filing-instruction text only renders when the
          section is EXPANDED AND has rows. Filing agents don't need
          the instructional preamble on every empty section; it's noise. */}
      {instructionKey && open && hasRows && (
        <div
          data-testid={`filing-section-instruction-${id}`}
          className="border-t border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 px-4 py-2 text-[11px] text-teal-700/80 dark:text-teal-300"
        >
          {t(instructionKey)}
        </div>
      )}
      {open && hasRows && (
        <div className="border-t border-slate-200 dark:border-slate-700 overflow-auto">{children}</div>
      )}
    </section>
  );
};

// v0.4.37 D3 — Monetary cell. Renders `—` for null/empty/zero
// monetary values; Period and Country cells use raw `<td>` (never
// this component) so the dash never replaces a non-monetary value.
// `tabular-nums` keeps right-aligned columns digit-aligned.
const isMoneyZero = (v) => {
  if (v === null || v === undefined || v === "") return true;
  const s = String(v).trim();
  return s === "0" || s === "0.00" || s === "-0.00" || s === "0.0";
};

// v0.6 — consecutive empty sections collapse into ONE summary line
// ("N sections — no activity this period"), expandable to reveal the
// original per-section rows. Sections with data are never grouped.
const EmptyRun = ({ nodes, titles }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="filing-empty-run" className="border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40">
      <button
        type="button"
        data-testid="filing-empty-run-toggle"
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center gap-2 px-4 py-2 text-left"
      >
        {open ? <CaretDown size={12} className="text-slate-500 dark:text-slate-400 shrink-0" /> : <CaretRight size={12} className="text-slate-500 dark:text-slate-400 shrink-0" />}
        <span className="text-[11px] text-slate-500 dark:text-slate-400 whitespace-nowrap shrink-0">
          {t("ui.filing.empty_run", { count: nodes.length, defaultValue: `${nodes.length} sections — no activity this period` })}
        </span>
        <span className="text-[10px] text-slate-400 dark:text-slate-600 truncate min-w-0 ml-1">
          · {titles.map((k) => t(k)).join(", ")}
        </span>
      </button>
      {open && <div className="border-t border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800/60">{nodes}</div>}
    </div>
  );
};

// Walk the ordered Section elements; collapse each RUN of 2+ consecutive
// empty ones (hasRows === false) into a single <EmptyRun>. Single empties
// and all data sections render unchanged, in place.
export const renderGrouped = (children) => {
  const out = [];
  let run = [];
  const flush = () => {
    if (!run.length) return;
    if (run.length === 1) out.push(run[0]);
    else out.push(
      <EmptyRun key={`empty-${run[0].props.id}`} nodes={run}
                titles={run.map((c) => c.props.titleKey)} />,
    );
    run = [];
  };
  children.forEach((c) => {
    if (c && c.props && c.props.hasRows === false) run.push(c);
    else { flush(); out.push(c); }
  });
  flush();
  return out;
};

const Cell = ({ v, dim }) => (
  <td className={`px-3 py-4 border-b border-slate-200 text-right tabular-nums ${dim ? "text-slate-500 dark:text-slate-400" : "text-slate-900 dark:text-slate-50"}`}>
    {isMoneyZero(v)
      ? <span className="text-slate-600 dark:text-slate-300" data-testid="money-cell-zero">—</span>
      : fmtMoney(v)}
  </td>
);

// v0.4.37 D2A — table header pass: bold teal accent, larger
// tracking, thicker dividing line. Sticky on vertical scroll
// (D2C) via `sticky top-0 z-10` on the row.
// v0.4.46 — Optional per-column title tooltip via `tips` map keyed by
// column label. Used for LOCAL GROSS to clarify it is informational.
const HeaderRow = ({ cols, tips = {} }) => (
  <tr className="bg-white dark:bg-slate-900 text-teal-700 dark:text-teal-300 text-[11px] tracking-normal font-bold sticky top-0 z-10">
    {cols.map((c, i) => (
      <th
        key={c}
        title={tips[c] || undefined}
        className={`px-3 py-3 border-b border-teal-200 ${i < 2 ? "text-left" : "text-right"} ${tips[c] ? "cursor-help underline decoration-dotted decoration-teal-600/40" : ""}`}
      >{c}</th>
    ))}
  </tr>
);

// v0.4.37 D3C — TOTAL row for the LOCAL SALES table. Sums every
// monetary column across the visible rows. Uses the same `Cell`
// component so the dash-for-zero rule (D3B) and the
// tabular-nums alignment (D3A) apply identically.
const LocalTotalRow = ({ rows }) => {
  const { t } = useTranslation();
  const sum = (key) => {
    let total = 0;
    for (const r of rows) {
      const raw = r[key];
      if (raw === undefined || raw === null || raw === "") continue;
      const n = Number(String(raw).replace(/,/g, ""));
      if (!Number.isNaN(n)) total += n;
    }
    return total === 0 ? "0.00" : total.toFixed(2);
  };
  return (
    <tr
      data-testid="filing-local-total-row"
      className="border-t-2 border-teal-300 dark:border-teal-700 bg-slate-50/80 dark:bg-slate-900/80 font-bold text-slate-900 dark:text-slate-50"
    >
      <td className="px-3 py-4 text-slate-500 dark:text-slate-400 text-xs tracking-normal font-bold" colSpan={2}>{t("ui.filing.total_row", { defaultValue: "Total" })}</td>
      <Cell v={sum("Taxable NET")} /><Cell v={sum("Taxable VAT")} />
      <Cell v={sum("Deductible NET")} dim /><Cell v={sum("Deductible VAT")} dim />
      <Cell v={sum("Zero-Rated NET")} /><Cell v={sum("Zero-Rated SELLER NET")} />
      {/* v0.4.46 — LOCAL GROSS dropped from TOTAL per operator sign-off
          2026-06-09. Summing an informational gross column across
          countries was misleading; the TOTAL row now sums only filing
          obligations. The visible per-country Local Gross cells are
          retained for traceability. */}
      <td className="px-3 py-4 border-b border-slate-200 dark:border-slate-700 text-right text-slate-600 dark:text-slate-300" data-testid="filing-local-total-localgross-suppressed">—</td>
    </tr>
  );
};

// Step 2.1 — corrections to earlier periods for one scheme (summary rows
// OSS_CORRECTION / IOSS_CORRECTION from engine/bucketed).
export const SchemeCorrections = ({ rows, testid }) => {
  const { t } = useTranslation();
  if (!rows.length) return null;
  return (
    <div data-testid={testid} className="mt-3">
      <p className="text-xs text-slate-600 dark:text-slate-300 mb-1">
        {t("ui.filing.scheme_corrections", { defaultValue: "Corrections to earlier periods (not in the figures above)" })}
      </p>
      <table className="w-full text-xs">
        <thead><HeaderRow cols={["Corrects period", "Member State", "VAT (EUR)"]} /></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.Country}-${r["Corrects Period"]}-${i}`}>
              <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">{r["Corrects Period"]}</td>
              <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100">{r.Country}</td>
              <Cell v={r["OSS VAT"]} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const FilingSections = ({ rows, countryFilter = "ALL", sectionFlagTotals = {}, onFlagBadgeClick }) => {
  const { t } = useTranslation();
  const [showAllCountries, setShowAllCountries] = useState(false);
  if (!rows || rows.length === 0) return null;
  // v0.4.20.6 Bug 1 — apply view-only country filter to displayed rows.
  // Backend always returns the full dataset; the filter is purely cosmetic
  // (it never affects calc/export data, only this table's rendering).
  // v0.6.6 — a prior comment here claimed "GB carries an implicit
  // always-show rule for the seller's local return," but no such
  // exemption was ever implemented (verified: no GB special-case exists
  // anywhere in the codebase) — the filter below is a plain equality
  // match against `countryFilter` with no exceptions. Setting the filter
  // to a non-GB country silently hides the seller's own GB local-return
  // row from this view, same as any other non-matching row would be.
  // Comment corrected to describe the actual behaviour rather than
  // implementing an unverified new carve-out.
  const cf = (countryFilter || "ALL").toUpperCase();
  const filtered = cf === "ALL"
    ? rows
    : rows.filter((r) => (r.Country || "").toUpperCase() === cf);
  const allLocal = filtered.filter((r) => r["Row Type"] === "LOCAL");
  const oss = filtered.filter((r) => r["Row Type"] === "OSS_INFO");
  const ioss = filtered.filter((r) => r["Row Type"] === "IOSS_INFO");
  // Step 2.1 (A2-589) — credit notes for an EARLIER period, listed under the
  // scheme's supplies and not netted into them (IR 282/2011 Art 61), as the
  // XML and the workbook file them.
  const ossCorrections = filtered.filter((r) => r["Row Type"] === "OSS_CORRECTION");
  const iossCorrections = filtered.filter((r) => r["Row Type"] === "IOSS_CORRECTION");
  // v0.4.33 U1 — hide zero-only rows from the LOCAL table by default
  // so a filing agent only sees countries that need filing. The
  // "Show all countries" toggle below the table reveals every row.
  // v0.4.46 — LOCAL visibility predicate now uses ONLY columns this
  // table renders. Previous version admitted rows that had non-zero
  // `B2B Sales (ICS)`, `Exports`, or `OSS NET` — but those display in
  // OTHER sections, so the row landed in LOCAL with nothing to show
  // except a confusing "Local Gross" figure. Operator sign-off
  // 2026-06-09 (FR row showing 1,549.94 with every obligation column
  // dashed): restrict admission to obligation columns this table
  // actually renders. `Local` (gross) is INTENTIONALLY excluded — a
  // country whose only non-zero figure is gross has nothing to file.
  const MONEY_KEYS_LOCAL = [
    "Taxable NET", "Taxable VAT",
    "Deductible NET", "Deductible VAT",
    "Zero-Rated NET", "Zero-Rated SELLER NET",
  ];
  const isNonZero = (r) => MONEY_KEYS_LOCAL.some(
    (k) => r[k] && r[k] !== "0.00" && r[k] !== "-0.00",
  );
  const local = showAllCountries ? allLocal : allLocal.filter(isNonZero);
  const hiddenCount = allLocal.length - local.length;
  const has = (k) => local.some((r) => r[k] && r[k] !== "0.00");

  // v0.4.22 F3 — render a "⚠ N flags" pill on a section header when
  // that section has any flagged rows. Click → onFlagBadgeClick(id)
  // scrolls the operator to the grouped review queue.
  const badge = (id) => {
    const n = sectionFlagTotals[id] || 0;
    if (n <= 0) return null;
    return (
      <span
        role="button"
        tabIndex={0}
        data-testid={`filing-section-flag-badge-${id}`}
        onClick={(e) => { e.stopPropagation(); onFlagBadgeClick?.(id); }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault(); e.stopPropagation(); onFlagBadgeClick?.(id);
          }
        }}
        className="ml-2 inline-flex items-center gap-1 bg-amber-600/20 border border-amber-600/50 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full text-[10px] tracking-normal hover:bg-amber-600/30 cursor-pointer"
        title={t("ui.filing.flag_badge_tooltip", { count: n })}
      >
        <WarningOctagon size={10} weight="bold" />
        {t("ui.filing.flag_badge", { count: n })}
      </span>
    );
  };

  const _all = (
    <>
      <Section id="01-local" titleKey="ui.filing.local_title" captionKey="ui.filing.local_caption" instructionKey="ui.filing.local_instruction" defaultOpen flagBadge={badge("01-local")} hasRows={local.length > 0}>
        <div className="overflow-auto max-h-[60vh]">
        <table className="w-full text-xs">
          <thead><HeaderRow
            cols={["Period", "Country", "Taxable NET", "Taxable VAT", "Deductible NET", "Deductible VAT", "Zero-Rated NET", "Zero-Rated SELLER NET", "Local Gross"]}
            tips={{
              "Local Gross": "Gross movement of locally-bucketed sales (zero-rated rows and refund-net contributions included). Informational only — your actual VAT obligation is on the NET/VAT columns to the left.",
            }}
          /></thead>
          <tbody>
            {local.map((r) => (
              <tr key={`local-${r.Country}`} data-testid={`local-row-${r.Country}`} className="hover:bg-white dark:bg-slate-900">
                <td className="px-3 py-4 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-xs">{r.Period}</td>
                <td className="px-3 py-4 border-b border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-50 font-bold text-sm">{r.Country}</td>
                <Cell v={r["Taxable NET"]} /><Cell v={r["Taxable VAT"]} />
                <Cell v={r["Deductible NET"]} dim /><Cell v={r["Deductible VAT"]} dim />
                <Cell v={r["Zero-Rated NET"]} /><Cell v={r["Zero-Rated SELLER NET"]} /><Cell v={r["Local"]} />
              </tr>
            ))}
            {/* v0.4.37 D3C — TOTAL row: thicker teal divider,
                bold slate-50 text, subtle highlight. Sums each
                monetary column across the visible rows. */}
            {local.length > 0 && (
              <LocalTotalRow rows={local} />
            )}
          </tbody>
        </table>
        </div>
        {/* v0.4.33 U1 — "Show all countries" toggle. Only renders
            inside the LOCAL section since this is the only table
            that gets a long all-zero tail in practice. */}
        {hiddenCount > 0 && (
          <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 flex items-center justify-between">
            <span className="text-[10px] text-slate-500 dark:text-slate-400">
              {t("ui.filing.countries_hidden", { count: hiddenCount, defaultValue: `${hiddenCount} countries hidden (zero obligations this period)` })}
            </span>
            <button
              type="button"
              data-testid="filing-local-show-all-countries"
              onClick={() => setShowAllCountries((s) => !s)}
              className="text-[10px] tracking-normal text-teal-700 dark:text-teal-300 hover:text-teal-700 dark:hover:text-teal-300 underline decoration-dotted"
            >
              {showAllCountries ? t("ui.filing.hide_all_zero", { defaultValue: "Hide all-zero" }) : t("ui.filing.show_all_countries", { defaultValue: "Show all countries" })}
            </button>
          </div>
        )}
      </Section>

      <Section id="02-b2b" titleKey="ui.filing.b2b_title" captionKey="ui.filing.b2b_caption" instructionKey="ui.filing.b2b_instruction" defaultOpen flagBadge={badge("02-b2b")} hasRows={has("B2B Sales (ICS)") || has("B2B Services (Art 44) NET")}>
        <table className="w-full text-xs">
          <thead><HeaderRow cols={["Period", "Country", "B2B Sales (ICS) Gross"]} /></thead>
          <tbody>
            {local.filter((r) => r["B2B Sales (ICS)"] && r["B2B Sales (ICS)"] !== "0.00").map((r) => (
              <tr key={`b2b-${r.Country}`} data-testid={`b2b-row-${r.Country}`}>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">{r.Period}</td>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-50">{r.Country}</td>
                <Cell v={r["B2B Sales (ICS)"]} />
              </tr>
            ))}
          </tbody>
        </table>
        {/* v0.5.7 (parity) — Art 44 B2B services are a separate ESL/VIES
            category from Art 138 goods (IE E1/E2, FR F1/F3, DE Kz 41/21…)
            and MUST render on-screen, not only in the export's ICS tab. */}
        {has("B2B Services (Art 44) NET") && (
          <table className="w-full text-xs border-t border-slate-200 dark:border-slate-700" data-testid="b2b-services-table">
            <thead><HeaderRow cols={["Period", "Country", "B2B Services (Art 44) NET"]} /></thead>
            <tbody>
              {local.filter((r) => r["B2B Services (Art 44) NET"] && r["B2B Services (Art 44) NET"] !== "0.00").map((r) => (
                <tr key={`b2bsvc-${r.Country}`} data-testid={`b2b-service-row-${r.Country}`}>
                  <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">{r.Period}</td>
                  <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-50">{r.Country}</td>
                  <Cell v={r["B2B Services (Art 44) NET"]} />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section id="03-oss" titleKey="ui.filing.oss_title" captionKey="ui.filing.oss_caption" instructionKey="ui.filing.oss_instruction" defaultOpen flagBadge={badge("03-oss")} hasRows={oss.length > 0 || ossCorrections.length > 0}>
        <table className="w-full text-xs">
          <thead><HeaderRow cols={["Period", "Destination", "OSS NET", "OSS VAT", "OSS GROSS", "OSS Txns"]} /></thead>
          <tbody>
            {oss.map((r) => (
              <tr key={`oss-${r.Country}`} data-testid={`oss-row-${r.Country}`}>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">{r.Period}</td>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-teal-700 dark:text-teal-300">{r.Country.replace("[OSS] ", "")}</td>
                <Cell v={r["OSS NET"]} /><Cell v={r["OSS VAT"]} /><Cell v={r["OSS GROSS"]} />
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-right text-slate-500 dark:text-slate-400">{r["OSS Txns"] ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <SchemeCorrections rows={ossCorrections} testid="oss-corrections" />
      </Section>

      <Section id="04a-b2b-refunds" titleKey="ui.filing.b2b_refunds_title" captionKey="ui.filing.b2b_refunds_caption" instructionKey="ui.filing.b2b_refunds_instruction" defaultOpen={false} flagBadge={badge("04a-b2b-refunds")} hasRows={has("B2B Refunds")}>
        <table className="w-full text-xs">
          <thead><HeaderRow cols={["Period", "Country", "B2B Refunds Gross"]} /></thead>
          <tbody>
            {local.filter((r) => r["B2B Refunds"] && r["B2B Refunds"] !== "0.00").map((r) => (
              <tr key={`b2br-${r.Country}`} data-testid={`b2b-refund-row-${r.Country}`}>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">{r.Period}</td>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-50">{r.Country}</td>
                <Cell v={r["B2B Refunds"]} />
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section id="04b-b2c-refunds" titleKey="ui.filing.b2c_refunds_title" captionKey="ui.filing.b2c_refunds_caption" instructionKey="ui.filing.b2c_refunds_instruction" defaultOpen={false} flagBadge={badge("04b-b2c-refunds")} hasRows={has("B2C Refunds")}>
        <table className="w-full text-xs">
          <thead><HeaderRow cols={["Period", "Country", "B2C Refunds Gross"]} /></thead>
          <tbody>
            {local.filter((r) => r["B2C Refunds"] && r["B2C Refunds"] !== "0.00").map((r) => (
              <tr key={`b2cr-${r.Country}`} data-testid={`b2c-refund-row-${r.Country}`}>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">{r.Period}</td>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-50">{r.Country}</td>
                <Cell v={r["B2C Refunds"]} />
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section id="05-rc" titleKey="ui.filing.rc_title" captionKey="ui.filing.rc_caption" instructionKey="ui.filing.rc_instruction" defaultOpen flagBadge={badge("05-rc")} hasRows={has("Reverse-Charge")}>
        <table className="w-full text-xs">
          <thead><HeaderRow cols={["Period", "Country", "Reverse-Charge Net"]} /></thead>
          <tbody>
            {local.filter((r) => r["Reverse-Charge"] && r["Reverse-Charge"] !== "0.00").map((r) => (
              <tr key={`rc-${r.Country}`} data-testid={`rc-row-${r.Country}`}>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">{r.Period}</td>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-50">{r.Country}</td>
                <Cell v={r["Reverse-Charge"]} />
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section id="06-exports" titleKey="ui.filing.exports_title" captionKey="ui.filing.exports_caption" instructionKey="ui.filing.exports_instruction" defaultOpen={false} flagBadge={badge("06-exports")} hasRows={has("Exports")}>
        <table className="w-full text-xs">
          <thead><HeaderRow cols={["Period", "Origin Country", "Exports Gross"]} /></thead>
          <tbody>
            {local.filter((r) => r["Exports"] && r["Exports"] !== "0.00").map((r) => (
              <tr key={`exp-${r.Country}`} data-testid={`exports-row-${r.Country}`}>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">{r.Period}</td>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-50">{r.Country}</td>
                <Cell v={r["Exports"]} />
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section id="07-marketplace" titleKey="ui.filing.marketplace_title" captionKey="ui.filing.marketplace_caption" instructionKey="ui.filing.marketplace_instruction" defaultOpen={false} flagBadge={badge("07-marketplace")} hasRows={has("Marketplace")}>
        <table className="w-full text-xs">
          <thead><HeaderRow cols={["Period", "Departure Country", "Marketplace Gross"]} /></thead>
          <tbody>
            {local.filter((r) => r["Marketplace"] && r["Marketplace"] !== "0.00").map((r) => (
              <tr key={`mp-${r.Country}`} data-testid={`marketplace-row-${r.Country}`}>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">{r.Period}</td>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-50">{r.Country}</td>
                <Cell v={r["Marketplace"]} />
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section id="08-ioss" titleKey="ui.filing.ioss_title" captionKey="ui.filing.ioss_caption" instructionKey="ui.filing.ioss_instruction" defaultOpen={ioss.length > 0 || iossCorrections.length > 0} flagBadge={badge("08-ioss")} hasRows={ioss.length > 0 || iossCorrections.length > 0}>
        <table className="w-full text-xs">
          <thead><HeaderRow cols={["Period", "Destination MS", "IOSS NET (EUR)", "IOSS VAT (EUR)", "IOSS GROSS (EUR)", "Txns"]} /></thead>
          <tbody>
            {ioss.map((r) => (
              <tr key={`ioss-${r.Country}`} data-testid={`ioss-row-${r.Country}`} className="hover:bg-white dark:bg-slate-900">
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">{r.Period}</td>
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-emerald-700 dark:text-emerald-300 font-bold">{r.Country}</td>
                <Cell v={r["OSS NET"]} />
                <Cell v={r["OSS VAT"]} />
                <Cell v={r["OSS GROSS"]} dim />
                <td className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-right">{r["OSS Txns"]}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <SchemeCorrections rows={iossCorrections} testid="ioss-corrections" />
      </Section>
    </>
  );
  return (
    <div data-testid="filing-sections" className="space-y-3">
      {renderGrouped(React.Children.toArray(_all.props.children))}
    </div>
  );
};
